import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {FOLDER_TAGS} from 'app/folders/api';
import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
  tagsRelatedTo,
} from 'app/store/sweet-api/utils';
import {DOCUMENTS_API_URL} from 'app/urls';
import {gt, ngt} from 'app/utils/i18n';

import {DEFAULT_PAGE_SIZE} from '../utils/constants';

import type {
  AddDocToFolderProps,
  BulkAddToFolderParams,
  BulkRecycleDocumentsParams,
  BulkRemoveFromFolderParams,
  DashboardFoldersFetchParamsType,
  DashboardLocationParamsType,
  DocumentFetchParamsType,
  RemoveDocProps,
} from 'app/documents/types';
import type {
  DocumentSerializedType,
  ListResponseType,
  UserJobSerializedType,
} from 'sweet-types';

export const DOCUMENT_TAG = 'document';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [DOCUMENT_TAG, FOLDER_TAGS.TAGS]})
  .injectEndpoints({
    endpoints: (build) => ({
      documents: build.query<
        ListResponseType<DocumentSerializedType>,
        DocumentFetchParamsType
      >({
        query: (params) => ({
          url: DOCUMENTS_API_URL,
          params,
        }),
        providesTags: providesTagsByURL(DOCUMENT_TAG),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load documents.'),
          })(args, api);
        },
      }),
      removeFromFolder: build.mutation<unknown, RemoveDocProps>({
        query: ({id}) => ({
          url: `${DOCUMENTS_API_URL}${id}/`,
          method: 'DELETE',
        }),
        invalidatesTags: (
          result,
          error,
          {document_content_type, documentURL}
        ) => [
          {type: DOCUMENT_TAG, id: 'list'},
          {type: FOLDER_TAGS.TAGS, id: 'list'},
          ...tagsRelatedTo({
            contentType: document_content_type,
            url: documentURL,
          }),
        ],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to remove the document.'),
            onSuccess: ({name}) =>
              gt('Document %s has been removed.', name || ''),
          })(args, api);
        },
      }),
      addToFolder: build.mutation<unknown, AddDocToFolderProps>({
        query: (body) => ({url: DOCUMENTS_API_URL, method: 'POST', body}),
        invalidatesTags: (
          result,
          error,
          {document_content_type, documentURL}
        ) => [
          {type: DOCUMENT_TAG, id: 'list'},
          {type: FOLDER_TAGS.TAGS, id: 'list'},
          ...tagsRelatedTo({
            contentType: document_content_type,
            url: documentURL,
          }),
        ],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to add to folder.'),
            onSuccess: () => gt('Added to the folder'),
          })(args, api);
        },
      }),
      getDocument: build.query<
        DocumentSerializedType,
        {id?: number; url?: string}
      >({
        query: ({id, url}) => ({
          url: url || `${DOCUMENTS_API_URL}/${id}/`,
        }),
        providesTags: (response) =>
          response != null
            ? [
                {
                  type: DOCUMENT_TAG,
                  id: response.url,
                },
              ]
            : [],
      }),
      deleteMembership: build.mutation<unknown, {url: string}>({
        query: ({url}) => ({
          url: url,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Removed document'),
          onError: () => gt('Unable to remove document at the moment'),
        }),
        invalidatesTags: () => [{type: DOCUMENT_TAG, id: 'list'}],
      }),
      bulkRecycleDocuments: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        BulkRecycleDocumentsParams
      >({
        queryFn: (
          {documents},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${DOCUMENTS_API_URL}bulk_recycle/`,
                    method: 'POST',
                    body: {
                      documents,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [{type: DOCUMENT_TAG, id: 'list'}],
        onQueryStarted: ({documents, ...args}, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              ngt(
                'The selected document is being moved to the recycle bin..',
                'The selected documents are being moved to the recycle bin...',
                documents.length
              ),
            onSuccess: () => [
              ngt(
                'The selected document has been moved to the recycle bin.',
                'The selected documents have been moved to the recycle bin.',
                documents.length
              ),
              {level: 'success'},
            ],
            onError: () =>
              ngt(
                "There was an error and we couldn't move the selected document to the recycle bin.",
                "There was an error and we couldn't move the selected documents to the recycle bin.",
                documents.length
              ),
          })(args, api);
        },
      }),
      bulkAddToFolder: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        BulkAddToFolderParams
      >({
        queryFn: (
          {documents},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${DOCUMENTS_API_URL}bulk_add_to_folder/`,
                    method: 'POST',
                    body: {
                      documents,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [{type: DOCUMENT_TAG, id: 'list'}],
        onQueryStarted: ({documents, ...args}, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              ngt(
                'The selected document is being added to the folder..',
                'The selected documents are being added to the folder...',
                documents.length
              ),
            onSuccess: () => [
              ngt(
                'The selected document has been added to the folder.',
                'The selected documents have been added to the folder.',
                documents.length
              ),
              {level: 'success'},
            ],
            onError: () =>
              ngt(
                "There was an error and we couldn't add the selected document to the folder.",
                "There was an error and we couldn't add the selected documents to the folder.",
                documents.length
              ),
          })(args, api);
        },
      }),
      bulkRemoveFromFolder: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        BulkRemoveFromFolderParams
      >({
        queryFn: (
          {documents},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${DOCUMENTS_API_URL}bulk_remove_from_folder/`,
                    method: 'POST',
                    body: {
                      documents,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [{type: DOCUMENT_TAG, id: 'list'}],
        onQueryStarted: ({documents, ...args}, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              ngt(
                'The selected document is being removed from the folder..',
                'The selected documents are being removed from the folder...',
                documents.length
              ),
            onSuccess: () => [
              ngt(
                'The selected document has been removed to the folder.',
                'The selected documents have been removed to the folder.',
                documents.length
              ),
              {level: 'success'},
            ],
            onError: () =>
              ngt(
                "There was an error and we couldn't remove the selected document from the folder.",
                "There was an error and we couldn't remove the selected documents from the folder.",
                documents.length
              ),
          })(args, api);
        },
      }),
    }),
  });

export const {
  useDocumentsQuery: useDocuments,
  useRemoveFromFolderMutation: useRemoveFromFolder,
  useAddToFolderMutation: useAddToFolder,
  useGetDocumentQuery: useDocument,
  useDeleteMembershipMutation: useDeleteDocumentMembership,
  useBulkRecycleDocumentsMutation: useBulkRecycleDocuments,
  useBulkAddToFolderMutation: useBulkAddToFolder,
  useBulkRemoveFromFolderMutation: useBulkRemoveFromFolder,
} = extendedAPI;

export const {useQueryState: useDocumentsState} =
  extendedAPI.endpoints.documents;

export const convertLocationParamsToFetchParams = (
  query: DashboardLocationParamsType
): DocumentFetchParamsType => {
  const filters = query.filter != null ? query.filter.split(',') : [];

  return {
    team_id:
      query.team_id != null && query.team_id !== '-1'
        ? query.team_id
        : undefined,
    in_no_teams: query.team_id === '-1' ? '1' : undefined,
    exclude_team_id: query.exclude_team_id,
    page: query.page != null ? query.page : '1',
    page_size:
      query.page_size != null ? query.page_size : `${DEFAULT_PAGE_SIZE}`,
    search: query.search,
    ordering: query.ordering,
    document_url: query.document_url,
    private:
      filters.indexOf('private') > -1
        ? 'true'
        : filters.indexOf('public') > -1
        ? 'false'
        : undefined,
    tag: query.tag != null ? query.tag : undefined,
    visible_to_user: query.visible_to_user,
    filter: query.filter,
    folder: query.folder || '', // forces to fetch from top level
  };
};

export const convertLocationParamsFoldersDocumentsToFetchParams = (
  query: DashboardLocationParamsType
): DashboardFoldersFetchParamsType => {
  return {
    page: query.folders_page != null ? query.folders_page : '1',
    page_size:
      query.folders_page_size != null
        ? query.folders_page_size
        : DEFAULT_PAGE_SIZE,
    parent: query.folder != null ? query.folder : '',
    team_id:
      query.team_id != null && query.team_id !== '-1'
        ? query.team_id
        : undefined,
    permission: query.permission,
    search: query.search ?? '',
  };
};
